<template>
  <div class="min-h-screen bg-cover bg-center bg-no-repeat relative" 
       :style="{ backgroundImage: 'url(/images/login-bg.jpg)' }">
    <!-- Dark overlay for better readability -->
    <div class="absolute inset-0 bg-black bg-opacity-40"></div>
    
    <!-- Header with branding and language switcher -->
    <div class="relative z-10 w-full px-4 sm:px-6 lg:px-8 py-6">
      <div class="flex justify-between items-center">
        <!-- SONNENGLAS branding -->
        <div class="text-white">
          <h1 class="text-lg font-semibold">SONNENGLAS® Partner Portal</h1>
        </div>
        
        <!-- Language switcher -->
        <div class="flex items-center space-x-2">
          <a href="#" 
             @click.prevent="switchLanguage('de')"
             class="text-white hover:text-gray-200 text-sm font-medium px-2 py-1 rounded"
             :class="{ 'bg-white bg-opacity-20': currentLocale === 'de' }">
            🇩🇪 DE
          </a>
          <a href="#" 
             @click.prevent="switchLanguage('en')"
             class="text-white hover:text-gray-200 text-sm font-medium px-2 py-1 rounded"
             :class="{ 'bg-white bg-opacity-20': currentLocale === 'en' }">
            🇺🇸 EN
          </a>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="relative z-10 min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div class="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div class="mb-6">
          <h2 class="text-center text-2xl font-bold text-gray-900 mb-2">
            Set New Password
          </h2>
          <p class="text-center text-sm text-gray-600">
            Enter your new password below.
          </p>
        </div>
        
        <form class="space-y-6" @submit.prevent="submit">
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <input
              id="email"
              v-model="form.email"
              name="email"
              type="email"
              autocomplete="email"
              required
              readonly
              class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 sm:text-sm"
            />
            <div v-if="form.errors.email" class="text-red-600 text-sm mt-1">
              {{ form.errors.email }}
            </div>
          </div>

          <div>
            <label for="password" class="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <input
              id="password"
              v-model="form.password"
              name="password"
              type="password"
              autocomplete="new-password"
              required
              class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Enter new password"
            />
            <div v-if="form.errors.password" class="text-red-600 text-sm mt-1">
              {{ form.errors.password }}
            </div>
          </div>

          <div>
            <label for="password_confirmation" class="block text-sm font-medium text-gray-700 mb-1">
              Confirm New Password
            </label>
            <input
              id="password_confirmation"
              v-model="form.password_confirmation"
              name="password_confirmation"
              type="password"
              autocomplete="new-password"
              required
              class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Confirm new password"
            />
            <div v-if="form.errors.password_confirmation" class="text-red-600 text-sm mt-1">
              {{ form.errors.password_confirmation }}
            </div>
          </div>

          <div>
            <button
              type="submit"
              :disabled="form.processing"
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="form.processing" class="flex items-center">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Resetting...
              </span>
              <span v-else>Reset Password</span>
            </button>
          </div>
          
          <!-- Back to login link -->
          <div class="text-center">
            <div class="text-sm text-gray-600">
              <a href="#" 
                 @click.prevent="goToLogin"
                 class="font-medium text-blue-600 hover:text-blue-500">
                Back to Sign in
              </a>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Footer branding -->
    <div class="absolute bottom-6 left-6 z-10">
      <div class="text-white text-sm opacity-80">
        <span class="font-semibold">Sonnenglas</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useForm, router } from '@inertiajs/vue3'
import { ref, onMounted, inject } from 'vue'

// Inject Ziggy route helper for script setup
const route = inject('route') as any

// Add type declaration for global route function

interface Props {
  token: string
  email: string
}

const { token, email } = defineProps<Props>()

const currentLocale = ref('en')

const form = useForm({
  token: token,
  email: email,
  password: '',
  password_confirmation: '',
})

onMounted(() => {
  // Get current locale from browser or localStorage
  currentLocale.value = localStorage.getItem('locale') || 'en'
})

const submit = () => {
  form.post(route('password.update'), {
    onFinish: () => {
      // Clear password fields on any response (success or error)
      form.reset('password', 'password_confirmation')
    },
  })
}

const switchLanguage = (locale: string) => {
  currentLocale.value = locale
  localStorage.setItem('locale', locale)
  
  // Redirect to current page with new locale
  const currentUrl = new URL(window.location.href)
  currentUrl.searchParams.set('lang', locale)
  window.location.href = currentUrl.toString()
}

const goToLogin = () => {
  router.get(route('login'))
}
</script>